import React from 'react';
import { ArrowDownAZ, ArrowUpAZ, ArrowUpDown, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export interface SortOption {
  value: string;
  label: string;
}

interface TableSortControlsProps {
  sortField: string;
  sortDirection: 'asc' | 'desc';
  options: SortOption[];
  onSortFieldChange: (field: string) => void;
  onSortDirectionToggle: () => void;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

export function TableSortControls({
  sortField,
  sortDirection,
  options,
  onSortFieldChange,
  onSortDirectionToggle,
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search...',
}: TableSortControlsProps) {
  const showSearch = typeof searchTerm === 'string' && !!onSearchChange;

  return (
    <div className="flex flex-wrap items-center gap-2 md:gap-3">
      {showSearch && (
        <div className="relative min-w-[240px] flex-1 md:max-w-[360px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="pl-9 pr-8"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      <span className="inline-flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
        <ArrowUpDown className="h-4 w-4" />
        Sort by
      </span>

      <Select value={sortField} onValueChange={onSortFieldChange}>
        <SelectTrigger className="w-[220px]">
          <SelectValue placeholder="Select field" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onSortDirectionToggle}
        className="gap-1.5"
        aria-label={`Sort direction ${sortDirection === 'asc' ? 'ascending' : 'descending'}`}
      >
        {sortDirection === 'asc' ? (
          <ArrowUpAZ className="h-4 w-4" />
        ) : (
          <ArrowDownAZ className="h-4 w-4" />
        )}
        {sortDirection === 'asc' ? 'Asc' : 'Desc'}
      </Button>
    </div>
  );
}

export default TableSortControls;
